
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
};

const SECURITY_STORAGE_KEY = 'bierz_security_logout';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('bierz_token'));

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const forceLogout = () => logout();

    window.addEventListener(SECURITY_STORAGE_KEY, forceLogout);

    return () => {
      window.removeEventListener(SECURITY_STORAGE_KEY, forceLogout);
    };
  }, []);

  const authHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: authHeaders()
      });

      if (response.data?.status === 'pending') {
        logout();
        throw new Error('Conta aguardando aprovação do administrador.');
      }

      if (response.data?.is_blocked) {
        logout();
        throw new Error('Conta bloqueada.');
      }

      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password
    });

    const { access_token, user: userData } = response.data;

    if (userData?.status === 'pending') {
      throw new Error('Sua conta ainda não foi aprovada.');
    }

    if (userData?.is_blocked) {
      throw new Error('Conta bloqueada pelo administrador.');
    }

    localStorage.setItem('bierz_token', access_token);
    setToken(access_token);
    setUser(userData);

    return userData;
  };

  // Cadastro agora depende de aprovação manual do admin
  const register = async (name, email, password, phone, address) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      name,
      email,
      password,
      phone,
      address,
      role: 'client',
      status: 'pending'
    });

    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('bierz_token');
    localStorage.setItem(SECURITY_STORAGE_KEY, Date.now().toString());
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (data) => {
    const response = await axios.put(`${API_URL}/api/auth/profile`, data, {
      headers: authHeaders()
    });

    setUser(response.data);

    return response.data;
  };

  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        register,
        logout,
        updateProfile,
        isAdmin,
        isAuthenticated: !!user
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
